import { Offer } from "./types";

/**
 * Filters offers based on white label visibility rules.
 *
 * Rules:
 * - is_white_label_offer = false/undefined → show everywhere (legacy behavior)
 * - is_white_label_offer = true:
 *   - white_label_visibility = "white_label_and_amber" → show on both WL and Amber
 *   - white_label_visibility = "white_label_only" → show only on WL (hide on Amber)
 *   - Partner check (on WL only):
 *     - white_label_partner_names is empty/null/["all"] → visible to ALL partners
 *     - Has specific names → only visible if requesting partner name matches
 */
const filterOffersByWhitelabel = (
  offers: Offer[],
  isWhitelabel: boolean,
  _partnerUuid?: string,
  partnerName?: string,
): Offer[] => {
  if (!offers?.length) return [];

  return offers.filter((offer) => {
    // Not a white label offer — show everywhere (legacy behavior)
    if (!offer.is_white_label_offer) return true;

    // is_white_label_offer = true — check visibility
    const visibility = offer.white_label_visibility;

    // Show on both White Label and Amber Global
    if (visibility === "white_label_and_amber") {
      // On Amber: always show (no partner check needed)
      if (!isWhitelabel) return true;
      // On White Label: check partner restriction
      return matchesPartner(offer, partnerName);
    }

    // white_label_only (default for WL offers) — hide on Amber Global
    if (!isWhitelabel) return false;

    // On White Label: check partner restriction
    return matchesPartner(offer, partnerName);
  });
};

/**
 * Checks if the current WL partner matches the offer's partner restrictions.
 * Uses white_label_partner_names (partner names from CRM).
 */
const matchesPartner = (offer: Offer, partnerName?: string): boolean => {
  const partnerNames = offer.white_label_partner_names;

  // No partner restriction set — show to all WL partners
  if (!partnerNames?.length || partnerNames.includes("all")) return true;

  // Check if current partner name matches any entry
  if (partnerName) {
    return partnerNames.some((name) => name.toLowerCase() === partnerName.toLowerCase());
  }

  return false;
};

export default filterOffersByWhitelabel;
